import React from "react";
import { ImportantNote, KeyTerm, Mnemonic } from "../note-components";
import { LatexText } from "../../../components/ui/latex-text";
import type { UnitNotes } from "../types";

const calculusAbUnit2Notes: UnitNotes = {
  unitId: "unit-2",
  unitName: "Unit 2: Differentiation: Definition and Fundamental Properties",
  examWeight: "12–15%",
  focus: "Derivatives, Rates of Change, Differentiation Rules",
  sections: [
    {
      id: "topic-2-1",
      title: "Topic 2.1: Defining Average and Instantaneous Rates of Change at a Point",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Average Rate of Change (AROC)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The average rate of change of a function <LatexText text="$f(x)$" /> over an interval <LatexText text="$[a, b]$" /> represents the slope of the <strong>secant line</strong> connecting the points <LatexText text="$(a, f(a))$" /> and <LatexText text="$(b, f(b))$" />.
            </p>
            <div className="space-y-3">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
                <h4 className="font-semibold text-slate-900">Formula</h4>
                <div className="mt-3 bg-white p-3 rounded border border-slate-200">
                  <LatexText text="$$AROC = \frac{f(b) - f(a)}{b - a}$$" />
                </div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900">Geometric Interpretation</h4>
                <p className="text-sm text-slate-700 mt-1">
                  It measures how much <LatexText text="$y$" /> changes per unit of <LatexText text="$x$" /> on average over a specific interval.
                </p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-900">Physics Context</h4>
                <p className="text-sm text-slate-700 mt-1">
                  If <LatexText text="$f(t)$" /> is position, the AROC is the <strong>average velocity</strong> over time interval <LatexText text="$[a, b]$" />.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. Instantaneous Rate of Change (IROC)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The instantaneous rate of change is the rate at which a function is changing at a specific single instant <LatexText text="$x = c$" />. This corresponds to the slope of the <strong>tangent line</strong> at that point.
            </p>
            <p className="text-slate-700 leading-relaxed mb-4">
              To find the IROC, we take the average rate of change over a tiny interval and shrink that interval to zero using a limit.
            </p>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
              <h4 className="font-semibold text-amber-900">Approximation</h4>
              <p className="text-sm text-slate-700 mt-1">
                We can approximate the slope of the tangent at <LatexText text="$x=c$" /> by calculating the slope of a secant line very close to <LatexText text="$c$" /> (e.g., between <LatexText text="$c$" /> and <LatexText text="$c + 0.001$" />).
              </p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-2",
      title: "Topic 2.2: Defining the Derivative of a Function and Using Derivative Notation",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. The Limit Definition of the Derivative</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The derivative of a function <LatexText text="$f$" /> at a point <LatexText text="$x$" />, denoted <LatexText text="$f'(x)$" />, is defined formally by the limit of the difference quotient. There are two standard forms you must recognize and be able to use.
            </p>
            
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Form A: The "h" definition (limit as step size goes to 0)</h4>
                <div className="mt-3 bg-white p-3 rounded border border-blue-200">
                  <LatexText text="$$f'(x) = \lim_{h \to 0} \frac{f(x+h) - f(x)}{h}$$" />
                </div>
                <ul className="list-disc pl-5 space-y-1 text-slate-700 text-sm mt-3">
                  <li>This form calculates the general derivative function <LatexText text="$f'(x)$" />.</li>
                  <li>To find the derivative at a specific point <LatexText text="$x=a$" />, substitute <LatexText text="$a$" /> for <LatexText text="$x$" />:
                    <div className="mt-2 bg-white p-2 rounded border border-blue-200">
                      <LatexText text="$$f'(a) = \lim_{h \to 0} \frac{f(a+h) - f(a)}{h}$$" />
                    </div>
                  </li>
                </ul>
              </div>

              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Form B: The "x approaches a" definition (alternative form at a specific point)</h4>
                <div className="mt-3 bg-white p-3 rounded border border-green-200">
                  <LatexText text="$$f'(a) = \lim_{x \to a} \frac{f(x) - f(a)}{x - a}$$" />
                </div>
                <p className="text-sm text-slate-700 mt-2">This form is strictly used to find the numerical value of the derivative at a specific constant <LatexText text="$a$" />.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. Derivative Notation</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Calculus uses different notations for differentiation, primarily credited to Leibniz and Lagrange. You must be fluent in all of them.
            </p>
            <p className="text-slate-700 leading-relaxed mb-4">
              If <LatexText text="$y = f(x)$" />:
            </p>
            <div className="space-y-3">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-semibold text-slate-900">Lagrange Notation (Primes)</h4>
                <p className="text-sm text-slate-700 mt-1"><LatexText text="$f'(x)$" /> or <LatexText text="$y'$" />. Used for functional emphasis.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-semibold text-slate-900">Leibniz Notation (Differentials)</h4>
                <p className="text-sm text-slate-700 mt-1"><LatexText text="$\frac{dy}{dx}$" /> or <LatexText text="$\frac{d}{dx}[f(x)]$" /></p>
                <ul className="list-disc pl-5 space-y-1 text-slate-700 text-sm mt-2">
                  <li>Read as "the derivative of <LatexText text="$y$" /> with respect to <LatexText text="$x$" />".</li>
                  <li>Useful for remembering slope (<LatexText text="$\frac{\Delta y}{\Delta x}$" />) and essential for Chain Rule and Related Rates.</li>
                </ul>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-semibold text-slate-900">Higher Order Derivatives</h4>
                <ul className="list-disc pl-5 space-y-1 text-slate-700 text-sm mt-2">
                  <li>Second derivative: <LatexText text="$f''(x)$" /> or <LatexText text="$\frac{d^2y}{dx^2}$" /></li>
                  <li>Third derivative: <LatexText text="$f'''(x)$" /> or <LatexText text="$\frac{d^3y}{dx^3}$" /></li>
                  <li>nth derivative: <LatexText text="$f^{(n)}(x)$" /> or <LatexText text="$\frac{d^ny}{dx^n}$" /></li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">3. Determining the Tangent Line Equation</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              To write the equation of a line tangent to <LatexText text="$f(x)$" /> at <LatexText text="$x = a$" />:
            </p>
            <div className="space-y-2 text-slate-700 mb-4">
              <p><strong>1. Point:</strong> Find <LatexText text="$y_1 = f(a)$" />.</p>
              <p><strong>2. Slope:</strong> Find <LatexText text="$m = f'(a)$" />.</p>
              <p><strong>3. Point-Slope Form:</strong> <LatexText text="$y - y_1 = m(x - x_1) \implies y - f(a) = f'(a)(x - a)$" />.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-3",
      title: "Topic 2.3: Estimating Derivatives of a Function at a Point",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Overview</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Sometimes we do not have an analytic function but are given data in a table or a graph.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Estimating from a Table</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              To estimate <LatexText text="$f'(c)$" /> given a table of values:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li>Choose two points as close to <LatexText text="$c$" /> as possible surrounding or adjacent to <LatexText text="$c$" />.</li>
              <li>Calculate the average rate of change (slope) between those two points.</li>
            </ul>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-bold text-blue-900">Example</h4>
              <p className="text-sm text-slate-700 mt-2">Estimate <LatexText text="$f'(3)$" /> given points <LatexText text="$(2, 4)$" /> and <LatexText text="$(4, 8)$" />.</p>
              <div className="mt-3 bg-white p-3 rounded border border-blue-200">
                <LatexText text="$$f'(3) \approx \frac{f(4)-f(2)}{4-2} = \frac{8-4}{2} = 2$$" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. Estimating from a Graph</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>Draw a rough tangent line at the point <LatexText text="$x=c$" />.</li>
              <li>Pick two points on that drawn line and calculate the slope.</li>
              <li><strong>Calculator:</strong> Graphing calculators can compute numeric derivatives (often labeled <LatexText text="$nDeriv$" />).</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-4",
      title: "Topic 2.4: Connecting Differentiability and Continuity",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Definition of Differentiability</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              A function <LatexText text="$f$" /> is differentiable at <LatexText text="$x=c$" /> if <LatexText text="$f'(c)$" /> exists. This means the limit of the difference quotient exists:
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <LatexText text="$$\lim_{x \to c^-} \frac{f(x) - f(c)}{x-c} = \lim_{x \to c^+} \frac{f(x) - f(c)}{x-c}$$" />
              <p className="text-sm text-slate-700 mt-3">(The slope from the left must equal the slope from the right).</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. Relationship Theorem</h3>
            <ImportantNote>
              <strong>"Differentiability implies Continuity."</strong>
              <p className="mt-2">If <LatexText text="$f$" /> is differentiable at <LatexText text="$x=c$" />, then <LatexText text="$f$" /> must be continuous at <LatexText text="$x=c$" />.</p>
            </ImportantNote>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 mt-4">
              <h4 className="font-bold text-amber-900">However, the converse is FALSE</h4>
              <p className="text-sm text-slate-700 mt-1">
                Continuity <strong>does not</strong> imply differentiability. A function can be continuous but not differentiable (e.g., a sharp corner).
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">3. Where Derivatives Fail to Exist</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              A function <LatexText text="$f(x)$" /> is <strong>not differentiable</strong> at <LatexText text="$x=c$" /> if:
            </p>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">1. Discontinuity</h4>
                <p className="text-sm text-slate-700 mt-1">The graph has a hole, jump, or asymptote at <LatexText text="$c$" />. (If the graph breaks, you cannot draw a tangent).</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">2. Corner/Cusp</h4>
                <p className="text-sm text-slate-700 mt-1">The graph has a sharp turn (e.g., <LatexText text="$f(x) = |x|$" /> at <LatexText text="$x=0$" />). The left-hand slope <LatexText text="$\neq$" /> right-hand slope.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">3. Vertical Tangent</h4>
                <p className="text-sm text-slate-700 mt-1">The graph becomes perfectly vertical at a point (e.g., <LatexText text="$f(x) = \sqrt[3]{x}$" /> at <LatexText text="$x=0$" />). The slope is undefined (infinite).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-5",
      title: "Topic 2.5: Applying the Power Rule",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Power Rule</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The Power Rule is the first shortcut for differentiation, bypassing the limit definition.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-900">The Rule</h4>
              <p className="text-sm text-slate-700 mt-2">If <LatexText text="$f(x) = x^n$" />, where <LatexText text="$n$" /> is any real number, then:</p>
              <div className="mt-3 bg-white p-3 rounded border border-blue-200">
                <LatexText text="$$\frac{d}{dx}(x^n) = n \cdot x^{n-1}$$" />
              </div>
            </div>

            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <h4 className="font-bold text-slate-900">Examples</h4>
              <ul className="list-disc pl-5 space-y-2 text-slate-700 text-sm mt-2">
                <li><LatexText text="$f(x) = x^5 \implies f'(x) = 5x^4$" /></li>
                <li><LatexText text="$f(x) = \frac{1}{x^2} = x^{-2} \implies f'(x) = -2x^{-3} = \frac{-2}{x^3}$" /></li>
                <li><LatexText text="$f(x) = \sqrt{x} = x^{1/2} \implies f'(x) = \frac{1}{2}x^{-1/2} = \frac{1}{2\sqrt{x}}$" /></li>
              </ul>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-6",
      title: "Topic 2.6: Derivative Rules: Constant, Sum, Difference, and Constant Multiple",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Differentiation Rules</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              These properties allow us to differentiate polynomials.
            </p>
            <div className="space-y-3">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">1. Constant Rule</h4>
                <p className="text-sm text-slate-700 mt-1">The derivative of a constant is 0.</p>
                <div className="mt-2 bg-white p-2 rounded border border-slate-200">
                  <LatexText text="$$\frac{d}{dx}[c] = 0$$" />
                </div>
                <p className="text-sm text-slate-600 mt-2 italic">(Reason: A horizontal line <LatexText text="$y=c$" /> has a slope of 0).</p>
              </div>

              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">2. Constant Multiple Rule</h4>
                <div className="mt-2 bg-white p-2 rounded border border-slate-200">
                  <LatexText text="$$\frac{d}{dx}[c \cdot f(x)] = c \cdot f'(x)$$" />
                </div>
              </div>

              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">3. Sum and Difference Rule</h4>
                <div className="mt-2 bg-white p-2 rounded border border-slate-200">
                  <LatexText text="$$\frac{d}{dx}[f(x) \pm g(x)] = f'(x) \pm g'(x)$$" />
                </div>
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg border border-green-200 mt-4">
              <h4 className="font-bold text-green-900">Example</h4>
              <p className="text-sm text-slate-700 mt-2">Differentiate <LatexText text="$y = 3x^4 - 2x + 7$" />.</p>
              <div className="mt-3 bg-white p-3 rounded border border-green-200">
                <LatexText text="$$y' = 3(4x^3) - 2(1) + 0 = 12x^3 - 2$$" />
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-7",
      title: "Topic 2.7: Derivatives of cos x, sin x, e^x, and ln x",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Foundational Derivatives</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              These are foundational memorization facts. You must know these by heart.
            </p>

            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">1. Trigonometric Derivatives (The Basics)</h4>
                <div className="mt-3 space-y-2">
                  <div className="bg-white p-2 rounded border border-blue-200">
                    <LatexText text="$$\frac{d}{dx}[\sin x] = \cos x$$" />
                  </div>
                  <div className="bg-white p-2 rounded border border-blue-200">
                    <LatexText text="$$\frac{d}{dx}[\cos x] = -\sin x$$" />
                  </div>
                </div>
                <p className="text-sm text-slate-700 mt-3 italic">Tip: The derivative of "co" functions (cosine, cosecant, cotangent) always results in a negative.</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">2. Exponential Derivative</h4>
                <div className="mt-3 bg-white p-3 rounded border border-green-200">
                  <LatexText text="$$\frac{d}{dx}[e^x] = e^x$$" />
                </div>
                <p className="text-sm text-slate-700 mt-2 italic">(This is the unique function that is its own derivative).</p>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-900">3. Logarithmic Derivative</h4>
                <div className="mt-3 bg-white p-3 rounded border border-purple-200">
                  <LatexText text="$$\frac{d}{dx}[\ln x] = \frac{1}{x}, \quad x > 0$$" />
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-8",
      title: "Topic 2.8: The Product Rule",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Product Rule</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              This rule is used when differentiating the <strong>product</strong> of two functions, <LatexText text="$f(x) \cdot g(x)$" />. <strong>Warning:</strong> The derivative is <em>not</em> simply <LatexText text="$f'(x) \cdot g'(x)$" />.
            </p>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-900">The Formula</h4>
              <div className="mt-3 bg-white p-3 rounded border border-blue-200">
                <LatexText text="$$\frac{d}{dx}[f(x)g(x)] = f(x)g'(x) + g(x)f'(x)$$" />
              </div>
              <p className="text-sm text-slate-700 mt-3 italic">Mnemonic: "First d-Second plus Second d-First"</p>
            </div>

            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-bold text-green-900">Example</h4>
              <p className="text-sm text-slate-700 mt-2">Find <LatexText text="$y'$" /> for <LatexText text="$y = x^2 \sin x$" />.</p>
              <div className="mt-3 bg-white p-3 rounded border border-green-200">
                <LatexText text="$$y' = (x^2)(\cos x) + (\sin x)(2x) = x^2\cos x + 2x\sin x$$" />
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-9",
      title: "Topic 2.9: The Quotient Rule",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Quotient Rule</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              This rule is used when differentiating the <strong>quotient</strong> of two functions, <LatexText text="$\frac{f(x)}{g(x)}$" />.
            </p>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-900">The Formula</h4>
              <div className="mt-3 bg-white p-3 rounded border border-blue-200">
                <LatexText text="$$\frac{d}{dx}\left[\frac{f(x)}{g(x)}\right] = \frac{g(x)f'(x) - f(x)g'(x)}{[g(x)]^2}$$" />
              </div>
              <p className="text-sm text-slate-700 mt-3 italic">Mnemonic: "Lo d-Hi minus Hi d-Lo, draw the line and square the Lo"</p>
            </div>

            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 mb-4">
              <h4 className="font-bold text-amber-900">Important</h4>
              <p className="text-sm text-slate-700 mt-1">Order matters in the numerator because of the subtraction.</p>
            </div>

            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-bold text-green-900">Example</h4>
              <p className="text-sm text-slate-700 mt-2">Differentiate <LatexText text="$y = \frac{x}{e^x}$" />.</p>
              <div className="mt-3 bg-white p-3 rounded border border-green-200">
                <LatexText text="$$y' = \frac{e^x(1) - x(e^x)}{(e^x)^2} = \frac{e^x(1-x)}{e^{2x}} = \frac{1-x}{e^x}$$" />
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-10",
      title: "Topic 2.10: Finding Derivatives of Tangent, Cotangent, Secant, and Cosecant Functions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Derivatives of Other Trigonometric Functions</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              While these can be derived using the Quotient Rule (e.g., <LatexText text="$\tan x = \frac{\sin x}{\cos x}$" />), they should be memorized for speed.
            </p>

            <div className="space-y-3">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">1. Tangent</h4>
                <div className="mt-2 bg-white p-2 rounded border border-slate-200">
                  <LatexText text="$$\frac{d}{dx}[\tan x] = \sec^2 x$$" />
                </div>
              </div>

              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">2. Cotangent</h4>
                <div className="mt-2 bg-white p-2 rounded border border-slate-200">
                  <LatexText text="$$\frac{d}{dx}[\cot x] = -\csc^2 x$$" />
                </div>
              </div>

              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">3. Secant</h4>
                <div className="mt-2 bg-white p-2 rounded border border-slate-200">
                  <LatexText text="$$\frac{d}{dx}[\sec x] = \sec x \tan x$$" />
                </div>
              </div>

              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900">4. Cosecant</h4>
                <div className="mt-2 bg-white p-2 rounded border border-slate-200">
                  <LatexText text="$$\frac{d}{dx}[\csc x] = -\csc x \cot x$$" />
                </div>
              </div>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mt-4">
              <h4 className="font-bold text-purple-900">Patterns for Memorization</h4>
              <ul className="list-disc pl-5 space-y-1 text-slate-700 text-sm mt-2">
                <li>Derivatives of "co" functions are negative.</li>
                <li><LatexText text="$\tan$" /> and <LatexText text="$\cot$" /> relate to squared terms (<LatexText text="$\sec^2, \csc^2$" />).</li>
                <li><LatexText text="$\sec$" /> and <LatexText text="$\csc$" /> relate to themselves multiplied by another trig function (<LatexText text="$\sec \tan, \csc \cot$" />).</li>
              </ul>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-summary",
      title: "Summary of Key Skills for Unit 2",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Essential Competencies</h3>
            <ul className="list-disc pl-6 space-y-3 text-slate-700">
              <li><strong>Calculate</strong> limits of difference quotients to find derivatives.</li>
              <li><strong>Interpret</strong> the derivative as an instantaneous rate of change or slope of a tangent line.</li>
              <li><strong>Identify</strong> points of non-differentiability on a graph (corners, cusps, discontinuities, vertical tangents).</li>
              <li><strong>Apply</strong> procedural rules (Power, Product, Quotient, Trig, Exp, Log) accurately and algebraically simplify the results if necessary.</li>
              <li><strong>Write</strong> tangent line equations using <LatexText text="$y - y_1 = f'(x_1)(x - x_1)$" />.</li>
            </ul>
          </section>
        </div>
      )
    }
  ]
};

export default calculusAbUnit2Notes;